/**
 * The single client transport — `BOUNDARIES.md` § 2, boundary test **B2**.
 *
 * This is the only module anywhere under `apps/web` that may import `firebase/functions`. Everything
 * the SPA ever asks the backend goes through `call`/`callTyped` as one `{ op, params }` envelope on
 * the callable named by the accepted packaging table (`ARCHITECTURE.md` § D-1), so there is exactly
 * one place where the operation contract, the request/response schemas and the eight stable error
 * codes are applied. `firebase/firestore` and `firebase/storage` appear nowhere in the client (B1) —
 * the security rules deny every client read and write, so a direct data path could only be a bug.
 *
 * All 68 operation shapes are frozen since T024. This module keeps the validation state explicit:
 *
 *   - `call(op, params)` returns `unknown` for a placeholder operation, and says in its result
 *     whether the value was actually validated;
 *   - `callTyped(...)` — the form that hands back a typed value — throws for an operation whose
 *     schema is still a placeholder, because a typed result there would be a lie.
 *
 * The Firebase SDK is reached through one injected seam (`CallableInvoker`), which is what lets the
 * envelope building, the schema application and the error decoding be unit-tested without a network,
 * an emulator or a project.
 */
import { getFunctions, httpsCallable, httpsCallableFromURL, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import { OPERATIONS, OPERATION_SCHEMAS, type ErrorCode, type OperationName } from '@simple-qa/shared';
import { TransportError, decodeCallableError, fieldIssuesFromThrown } from './errors';
import {
  EMULATOR_HOSTS,
  FUNCTIONS_REGION,
  currentEnv,
  emulatorsEnabled,
  getFirebaseApp,
} from './firebase';

/* ------------------------------------------------------------------ *
 * The callable surface
 * ------------------------------------------------------------------ */

/**
 * The three deployed functions a browser may call, of the eight. `aiWorker`, `hourlyAiRun`,
 * `purgeDaily` and `uploadSweep` are background triggers and `fileGateway` is a streaming endpoint —
 * none of them takes an envelope from a client, so an operation served by one of them is refused here
 * rather than attempted.
 */
export const CALLABLE_FUNCTIONS = Object.freeze(['bootstrapApi', 'studentApi', 'adminApi'] as const);

export type CallableFunctionName = (typeof CALLABLE_FUNCTIONS)[number];

export function isCallableFunction(name: string): name is CallableFunctionName {
  return (CALLABLE_FUNCTIONS as readonly string[]).includes(name);
}

/** The wire shape the dispatcher parses (`functions/src/lib/dispatch.ts`). */
export interface OperationEnvelopeWire {
  readonly op: OperationName;
  readonly params: unknown;
}

/** The seam over `httpsCallable`. One function, so a test can supply its own. */
export type CallableInvoker = (fn: CallableFunctionName, envelope: OperationEnvelopeWire) => Promise<unknown>;

/**
 * The structural shape of a `@simple-qa/shared` Zod schema, as this module uses it.
 *
 * Declaring the seam structurally rather than importing `zod` keeps the validation library out of
 * the client's dependency list while still giving `callTyped` a real, inferred result type: a Zod
 * schema satisfies this interface exactly.
 */
export interface ContractSchema<T> {
  readonly parse: (value: unknown) => T;
}

/* ------------------------------------------------------------------ *
 * The client
 * ------------------------------------------------------------------ */

/** What `call` returns: the value, plus whether a real schema actually validated it. */
export interface CallResult {
  readonly op: OperationName;
  readonly data: unknown;
  /** Always true on the frozen T024 surface; retained to keep the transport result self-describing. */
  readonly validated: boolean;
}

export interface OperationClient {
  /** The general form. `data` is `unknown`, and `validated` says whether it was really checked. */
  call: (op: OperationName, params: unknown) => Promise<CallResult>;
  /** The typed form. Only usable for an operation whose contract shape is frozen. */
  callTyped: <Req, Res>(
    op: OperationName,
    request: ContractSchema<Req>,
    response: ContractSchema<Res>,
    params: Req,
  ) => Promise<Res>;
}

export type AccountStatus = 'pending' | 'approved' | 'rejected' | 'deactivated';
export type AccountRole = 'student' | 'admin';

/** The narrow server-owned access decision the authenticated shell may render. */
export interface AccountAccessSnapshot {
  readonly status: AccountStatus;
  readonly role: AccountRole;
}

export interface AccountBootstrapResult extends AccountAccessSnapshot {
  readonly enrollmentsCreated: number;
  readonly invitesConsumed: number;
  readonly conflicts: readonly Readonly<Record<string, unknown>>[];
}

export interface AccountApi {
  readonly bootstrap: () => Promise<AccountBootstrapResult>;
  readonly state: () => Promise<AccountAccessSnapshot>;
}

/**
 * Request and response types inferred directly from the frozen shared registry.
 *
 * Feature code gets useful static types without importing Zod or restating a contract locally.
 * The runtime schemas are still the exact objects the server dispatches with.
 */
export type FeatureOperationName = keyof typeof OPERATION_SCHEMAS & OperationName;
export type OperationRequest<Name extends FeatureOperationName> = ReturnType<
  (typeof OPERATION_SCHEMAS)[Name]['request']['parse']
>;
export type OperationResponse<Name extends FeatureOperationName> = ReturnType<
  (typeof OPERATION_SCHEMAS)[Name]['response']['parse']
>;

/**
 * `issuePrefix` keeps a locally rejected request describing itself the way the server would: the
 * dispatcher validates the whole envelope and reports `params.marker`, so a client-side rejection of
 * the params says `params.marker` too. A response failure has no envelope path, so it prefixes
 * `response`.
 */
function parseOrThrow<T>(
  schema: ContractSchema<T>,
  value: unknown,
  code: ErrorCode,
  what: string,
  issuePrefix: string,
): T {
  try {
    return schema.parse(value);
  } catch (error) {
    throw new TransportError(code, what, error, fieldIssuesFromThrown(error, issuePrefix));
  }
}

/**
 * Build a client over one invoker. Exported so tests drive the whole path — envelope, schemas,
 * error decoding — with no Firebase involved, and so a later task can wrap the transport if it
 * genuinely needs to.
 */
export function createOperationClient(invoke: CallableInvoker): OperationClient {
  function target(op: OperationName): CallableFunctionName {
    const servedBy = OPERATIONS[op].servedBy;
    if (!isCallableFunction(servedBy)) {
      // A background operation (`system.*`) has no client entry point at all. Refusing locally is
      // not an optimisation: it keeps the client from ever presenting a request the server would
      // have to reject, and makes that a testable property instead of a convention.
      throw new TransportError('FORBIDDEN', `"${op}" is served by ${servedBy}, which no client may call`);
    }
    return servedBy;
  }

  async function call(op: OperationName, params: unknown): Promise<CallResult> {
    const spec = OPERATIONS[op];
    const fn = target(op);
    const validatedParams = parseOrThrow(
      spec.request as ContractSchema<unknown>,
      params,
      'INVALID_ARGUMENT',
      `the params of "${op}" do not match its request schema`,
      'params',
    );

    let raw: unknown;
    try {
      raw = await invoke(fn, { op, params: validatedParams });
    } catch (error) {
      throw decodeCallableError(error);
    }

    if (spec.schemaStatus !== 'specified') {
      throw new TransportError('INTERNAL', `"${op}" still carries a placeholder schema`);
    }
    const data = parseOrThrow(
      spec.response as ContractSchema<unknown>,
      raw,
      'INTERNAL',
      `the response of "${op}" does not match its response schema`,
      'response',
    );
    return Object.freeze({ op, data, validated: true });
  }

  async function callTyped<Req, Res>(
    op: OperationName,
    request: ContractSchema<Req>,
    response: ContractSchema<Res>,
    params: Req,
  ): Promise<Res> {
    const spec = OPERATIONS[op];
    // The same identity check `defineHandler` makes on the server: the caller must hand over the
    // registry's own schemas, not a local restatement that has drifted from them. Restating the
    // schema at the call site is what produces a real type; asserting identity is what stops that
    // restatement from becoming a second contract.
    if ((spec.request as unknown) !== (request as unknown) || (spec.response as unknown) !== (response as unknown)) {
      throw new TransportError(
        'INTERNAL',
        `callTyped("${op}") was given schemas that are not the ones @simple-qa/shared dispatches with`,
      );
    }
    if (spec.schemaStatus !== 'specified') {
      throw new TransportError(
        'INTERNAL',
        `"${op}" still carries a placeholder schema; a typed result cannot be promised for it`,
      );
    }
    const result = await call(op, params);
    return response.parse(result.data);
  }

  return { call, callTyped };
}

/**
 * The two authentication-stage operations, bound to the frozen shared schemas.
 *
 * Keeping these calls here is an executable part of B2: auth UI may ask for bootstrap/state, but it
 * still cannot import Firebase Functions or restate the wire contract in a component.
 */
export function createAccountApi(source: Pick<OperationClient, 'callTyped'>): AccountApi {
  const bootstrapSpec = OPERATIONS['account.bootstrap'];
  const stateSpec = OPERATIONS['me.state'];
  return Object.freeze({
    bootstrap: () =>
      source.callTyped<Record<string, never>, AccountBootstrapResult>(
        'account.bootstrap',
        bootstrapSpec.request as ContractSchema<Record<string, never>>,
        bootstrapSpec.response as ContractSchema<AccountBootstrapResult>,
        {},
      ),
    state: () =>
      source.callTyped<Record<string, never>, AccountAccessSnapshot>(
        'me.state',
        stateSpec.request as ContractSchema<Record<string, never>>,
        stateSpec.response as ContractSchema<AccountAccessSnapshot>,
        {},
      ),
  });
}

/* ------------------------------------------------------------------ *
 * The Firebase-backed instance
 * ------------------------------------------------------------------ */

let functionsInstance: Functions | null = null;

function firebaseFunctions(): Functions {
  if (functionsInstance !== null) return functionsInstance;
  const instance = getFunctions(getFirebaseApp(), FUNCTIONS_REGION);
  if (emulatorsEnabled(currentEnv())) {
    connectFunctionsEmulator(instance, EMULATOR_HOSTS.functionsHost, EMULATOR_HOSTS.functionsPort);
  }
  functionsInstance = instance;
  return instance;
}

/**
 * *(staging, 2026-09-26)* A deployed build calls its functions through the site's own address.
 *
 * Firebase Hosting rewrites `/api/<function>` to the function (`firebase.deploy.json`). A call to
 * `cloudfunctions.net` is cross-origin, and the browser sends a CORS preflight before every one of
 * them (the callable's CORS reply sets no `Access-Control-Max-Age`, so no preflight is reused for long).
 * On the owner's connection each round trip cost about a second, so every operation cost two. Same
 * origin means no preflight: one round trip per operation. The emulator suite keeps its direct URL.
 */
export function sameOriginCallableUrl(fn: string, origin: string = window.location.origin): string {
  return `${origin}/api/${fn}`;
}

/**
 * The real invoker. `httpsCallable` attaches the ID token of the Auth instance bound to the same
 * `FirebaseApp` — which is exactly why `src/lib/firebase.ts` hands out one app and only one.
 */
const firebaseInvoker: CallableInvoker = async (fn, envelope) => {
  const functions = firebaseFunctions();
  const callable = emulatorsEnabled(currentEnv())
    ? httpsCallable<OperationEnvelopeWire, unknown>(functions, fn)
    : httpsCallableFromURL<OperationEnvelopeWire, unknown>(functions, sameOriginCallableUrl(fn));
  const result = await callable(envelope);
  return result.data;
};

const client = createOperationClient(firebaseInvoker);
const accountClient = createAccountApi(client);

/**
 * Typed access to any frozen browser-callable operation.
 *
 * This is the feature-facing transport: every admin panel uses it instead of importing Firebase
 * or selecting its own callable. `callTyped` asserts schema identity before the request can leave
 * the browser, so the generic typing here cannot become a second wire contract.
 */
export function callOperation<Name extends FeatureOperationName>(
  op: Name,
  params: OperationRequest<Name>,
): Promise<OperationResponse<Name>> {
  const spec = OPERATION_SCHEMAS[op];
  return client.callTyped(
    op,
    spec.request as unknown as ContractSchema<OperationRequest<Name>>,
    spec.response as unknown as ContractSchema<OperationResponse<Name>>,
    params,
  );
}

/** The application's transport. Nothing else in `apps/web` talks to the backend. */
export const call = client.call;
export const callTyped = client.callTyped;
export const bootstrapCurrentAccount = accountClient.bootstrap;
export const readCurrentAccountState = accountClient.state;

/** Re-exported so a caller that already imports the transport does not need a second import. */
export { TransportError, decodeCallableError, parseFieldIssues, type FieldIssue } from './errors';
